import { CurlAssessment, RockColor, ShotResult, ShotType, Weight } from '../types/curling';
import { api } from './client';

/**
 * The public live-games spectator feature — see {@code LiveGamesController} on the
 * backend. `fetchDashboard` needs no session at all; `fetchDetail` needs one (any
 * account, any role) but nothing account-specific, since neither is scoped to the
 * caller's own accounts the way every other read in this app is.
 */
export interface LiveGameSummary {
  id: string;
  curlingClub?: string;
  sheetNumber?: string;
  team1Name: string;
  team2Name: string;
  team1RockColor?: RockColor;
  team2RockColor?: RockColor;
  team1Score: number;
  team2Score: number;
  endsPlayed: number;
  complete: boolean;
  lastActivityAt: number;
}

export interface LiveGamesDashboard {
  live: LiveGameSummary[];
  recentlyCompleted: LiveGameSummary[];
}

export interface LiveGameEndScore {
  number: number;
  team1Score: number;
  team2Score: number;
  /** False for an end the recorder's client has pre-created but not yet played. */
  completed: boolean;
  /** Rocks each team has thrown so far this end — backs the rock tracker. */
  team1ShotsThrown: number;
  team2ShotsThrown: number;
  hammerOverride?: 'team1' | 'team2';
}

/** A team's BRC weight/curl execution score — the unweighted mean of its players' scores. */
export interface LiveGameTeamScore {
  overallScore?: number;
  weightControlScore?: number;
  curlControlScore?: number;
}

/** One player's BRC weight/curl execution breakdown — see BRC Scoring - Teams & Players
 * for the same figures computed from an account's own full game data. */
export interface LiveGamePlayerScore {
  playerName: string;
  team: 'team1' | 'team2';
  totalShots: number;
  weightControlScore?: number;
  curlControlScore?: number;
  overallScore?: number;
  accuracyPercent?: number;
}

/**
 * A live "who's throwing right now" snapshot for one shot slot, broadcast in three
 * stages as the recorder works through it (see `pushCurrentShot`): identity only at
 * first, then `shotType`/`expectedTime`/`expectedCurl` once Analyze Shot is pressed,
 * then `weight`/`curlAssessment`/`result` once Record Shot is pressed.
 */
export interface CurrentShot {
  endNumber: number;
  team: 'team1' | 'team2';
  playerName: string;
  shotNumber: number;
  shotType?: ShotType;
  expectedTime?: number;
  expectedCurl?: number;
  /** Where the rock actually finished, distinct from `shotType` (the called target
   * zone) — only known once the shot has been thrown. */
  actualZone?: ShotType;
  weight?: Weight;
  curlAssessment?: CurlAssessment;
  result?: ShotResult;
  updatedAt: number;
}

export interface LiveGameDetail {
  id: string;
  curlingClub?: string;
  sheetNumber?: string;
  team1Name: string;
  team2Name: string;
  team1Players: string[];
  team2Players: string[];
  team1RockColor?: RockColor;
  team2RockColor?: RockColor;
  startsWithHammer?: 'team1' | 'team2';
  ends: LiveGameEndScore[];
  team1TotalScore: number;
  team2TotalScore: number;
  complete: boolean;
  date: number;
  /** Epoch ms of the earliest recorded shot — when play actually started, distinct
   * from `date` (game setup time). Undefined until at least one shot is recorded. */
  firstShotAt?: number;
  team1Score: LiveGameTeamScore;
  team2Score: LiveGameTeamScore;
  players: LiveGamePlayerScore[];
  /** Newest first, current shot plus the two before it. Empty until the first shot of
   * the game has been started. */
  currentShotHistory: CurrentShot[];
}

export const fetchLiveGamesDashboard = (): Promise<LiveGamesDashboard> =>
  api.get<LiveGamesDashboard>('/live-games');

export const fetchLiveGameDetail = (gameId: string): Promise<LiveGameDetail> =>
  api.get<LiveGameDetail>(`/live-games/${encodeURIComponent(gameId)}`);

/**
 * Pushed from the recording screens (see ShotTracker.tsx) at each of the three
 * lifecycle moments — never awaited for its result, and failures are swallowed by the
 * caller: this is a best-effort live broadcast, not part of the durable game record,
 * so it must never block or interrupt actually recording the shot.
 */
export interface CurrentShotUpdate {
  endNumber: number;
  team: 'team1' | 'team2';
  playerName: string;
  shotNumber: number;
  shotType?: ShotType;
  expectedTime?: number;
  expectedCurl?: number;
  actualZone?: ShotType;
  weight?: Weight;
  curlAssessment?: CurlAssessment;
  result?: ShotResult;
}

export const pushCurrentShot = (gameId: string, update: CurrentShotUpdate): Promise<void> =>
  api.put<void>(`/games/${encodeURIComponent(gameId)}/current-shot`, update);
